// Os Relatórios da Central do Perito (fase C): o resumo executivo imprimível, a planilha das perícias, as perícias
// por tribunal e as nomeadas e concluídas por mês. Portado de `src/routes/_authenticated/relatorios.tsx`, de
// `src/lib/relatorios.ts` (`montarProdutividade`, `dataConclusao`) e de
// `src/components/reports/RelatorioExecutivoPDF.tsx` da Central do Perito, no commit f5c1923. PURO, com o relógio
// por parâmetro e prova própria (`tests/perito-relatorios.mjs`).
//
// O que muda de lá, e por quê:
//
//  - CONCLUÍDA NO MÊS é o mês em que a perícia foi para a etapa 10, lido no histórico ("Etapa alterada para 10").
//    A tela de lá contava o mês da ÚLTIMA EDIÇÃO da perícia concluída: corrigir uma vírgula em dezembro mudava a
//    conclusão de agosto para dezembro. A regra do histórico já estava escrita lá (`dataConclusao`), e a tela
//    não a usava. Aqui vale a passagem MAIS RECENTE à etapa 10 — a perícia reaberta e concluída de novo conta
//    quando terminou —, e a perícia que saiu da etapa 10 não conta.
//  - Os meses são os de São Paulo (`datas.ts`); lá saíam de `getMonth()` sobre datas lidas em UTC.
//  - O previsto e o recebido por mês são os da fase B (`serieMensal`): o recebido conta no mês em que ENTROU. O
//    PDF de lá somava os dois no mês do vencimento.
//  - O resumo executivo sai pela impressão do navegador, e não por `@react-pdf/renderer`: o hub imprime assim em
//    toda parte (o currículo, o recibo do DP, a lista de concursos), e uma segunda maneira de gerar papel é uma
//    segunda maneira de ele sair diferente. O ranking por tribunal do PDF tinha os 8 primeiros; aqui, todos.
//  - A planilha é CSV (`lib/csv.ts`), que o Excel abre; lá eram três formatos.
import { numeroParaCsv } from "../csv";
import { diaDe, formatarData, hoje, mesDe } from "./datas";
import { serieMensal, totaisDeHonorarios, todosOsHonorarios } from "./financeiro";
import { PRIORIDADE_LABEL, STATUS_PERICIA_LABEL } from "./format";
import { ETAPAS, ETAPA_LABEL, etapaAtiva, etapaNumDe, statusDaEtapa, type EtapaNum } from "./pipeline";
import type { HistoricoEntry, Pericia } from "./tipos";

const SEM_TRIBUNAL = "Sem tribunal";

/** Todas as perícias por tribunal, da que mais tem à que menos; o empate é alfabético. */
export function periciasPorTribunal(pericias: Pericia[]): Array<{ nome: string; quantas: number }> {
  const mapa = new Map<string, number>();
  for (const p of pericias) mapa.set(p.tribunal || SEM_TRIBUNAL, (mapa.get(p.tribunal || SEM_TRIBUNAL) ?? 0) + 1);
  return [...mapa.entries()].map(([nome, quantas]) => ({ nome, quantas })).sort((a, b) => b.quantas - a.quantas || a.nome.localeCompare(b.nome, "pt-BR"));
}

const PASSOU_PARA_10 = /^Etapa alterada para 10(?:\.|$)/;

/**
 * O dia em que a perícia foi concluída: a passagem mais recente à etapa 10 no histórico. Sem essa linha (a
 * perícia que já nasceu concluída), o dia da última mudança, como lá. Nulo se a perícia não está concluída.
 */
export function diaDaConclusao(p: Pericia, historico: HistoricoEntry[]): string | null {
  if (etapaAtiva(p)) return null;
  const passagens = historico.filter((h) => h.pericia_id === p.id && PASSOU_PARA_10.test(h.acao)).map((h) => diaDe(h.ts)).filter((d): d is string => !!d).sort();
  return passagens.at(-1) ?? diaDe(p.atualizadoEm);
}

export interface MesDeProdutividade {
  /** AAAA-MM */
  mes: string;
  nomeadas: number;
  concluidas: number;
}

/** Os últimos `n` meses: as nomeadas (pela nomeação e, sem ela, pelo cadastro) e as concluídas. */
export function produtividadePorMes(pericias: Pericia[], historico: HistoricoEntry[], n: number, agora: Date = new Date()): MesDeProdutividade[] {
  const meses = serieMensal([], n, agora).map((m) => ({ mes: m.mes, nomeadas: 0, concluidas: 0 }));
  const porMes = new Map(meses.map((m) => [m.mes, m]));
  for (const p of pericias) {
    const nomeada = porMes.get(mesDe(p.dataNomeacao) ?? mesDe(p.criadoEm) ?? "");
    if (nomeada) nomeada.nomeadas += 1;
    const concluida = porMes.get(mesDe(diaDaConclusao(p, historico)) ?? "");
    if (concluida) concluida.concluidas += 1;
  }
  return meses;
}

export interface ResumoExecutivo {
  geradoEm: string;
  total: number;
  emAndamento: number;
  concluidas: number;
  honorarios: number;
  recebido: number;
  aReceber: number;
  porEtapa: Array<{ etapa: EtapaNum; rotulo: string; quantas: number }>;
  porTribunal: Array<{ nome: string; quantas: number }>;
  /** Os 6 meses, do mais antigo ao de hoje: o que vence e o que entrou em cada um. */
  meses: Array<{ mes: string; previsto: number; recebido: number }>;
  pericias: LinhaDoRelatorio[];
}

export interface LinhaDoRelatorio {
  numero: string;
  apelido: string;
  tribunal: string;
  vara: string;
  tipo: string;
  etapa: string;
  situacao: string;
  prioridade: string;
  nomeacao: string;
  honorarios: number;
  recebido: number;
  criadoEm: string;
}

/** Uma linha por perícia, na ordem: as em andamento primeiro, e o nome dentro de cada grupo. */
export function linhasDoRelatorio(pericias: Pericia[]): LinhaDoRelatorio[] {
  return [...pericias]
    .sort((a, b) => Number(!etapaAtiva(a)) - Number(!etapaAtiva(b)) || (a.apelido || a.numeroProcesso).localeCompare(b.apelido || b.numeroProcesso, "pt-BR"))
    .map((p) => {
      const e = etapaNumDe(p);
      const t = totaisDeHonorarios(p.honorarios);
      return {
        numero: p.numeroProcesso || "(sem número)",
        apelido: p.apelido ?? "",
        tribunal: p.tribunal,
        vara: p.vara,
        tipo: p.tipoPericia,
        etapa: `${e}. ${ETAPA_LABEL[e]}`,
        // A situação acompanha a etapa (000385), e não o `status` gravado.
        situacao: STATUS_PERICIA_LABEL[statusDaEtapa(e)],
        prioridade: PRIORIDADE_LABEL[p.prioridade] ?? p.prioridade,
        nomeacao: formatarData(p.dataNomeacao),
        honorarios: t.total,
        recebido: t.recebido,
        criadoEm: formatarData(p.criadoEm),
      };
    });
}

export function resumoExecutivo(pericias: Pericia[], agora: Date = new Date()): ResumoExecutivo {
  const t = totaisDeHonorarios(todosOsHonorarios(pericias));
  const ativas = pericias.filter((p) => etapaAtiva(p)).length;
  return {
    geradoEm: hoje(agora),
    total: pericias.length,
    emAndamento: ativas,
    concluidas: pericias.length - ativas,
    honorarios: t.total,
    recebido: t.recebido,
    aReceber: t.aReceber,
    porEtapa: ETAPAS.map((etapa) => ({ etapa, rotulo: ETAPA_LABEL[etapa], quantas: pericias.filter((p) => etapaNumDe(p) === etapa).length })),
    porTribunal: periciasPorTribunal(pericias),
    meses: serieMensal(pericias, 6, agora),
    pericias: linhasDoRelatorio(pericias),
  };
}

/** A planilha das perícias: o cabeçalho e as linhas, para `montarCsv`. Os valores com a vírgula do Excel do Brasil. */
export function planilhaDasPericias(pericias: Pericia[]): { cabecalho: string[]; linhas: unknown[][] } {
  return {
    cabecalho: ["Número do processo", "Apelido", "Tribunal", "Vara", "Tipo de perícia", "Etapa", "Situação", "Prioridade", "Nomeação", "Honorários", "Recebido", "Cadastrada em"],
    linhas: linhasDoRelatorio(pericias).map((l) => [l.numero, l.apelido, l.tribunal, l.vara, l.tipo, l.etapa, l.situacao, l.prioridade, l.nomeacao, numeroParaCsv(l.honorarios), numeroParaCsv(l.recebido), l.criadoEm]),
  };
}

// ── O resumo executivo para imprimir ─────────────────────────────────────────────────────────────────────

/**
 * Todo valor passa por aqui: o HTML vai para uma janela da MESMA origem do hub (`document.write`), e o apelido,
 * a vara e o nome das partes são digitados pelo aluno. Um `<script>` num apelido rodaria com a sessão de quem
 * imprime. Não existe interpolação crua nesta folha.
 */
export function escapar(v: unknown): string {
  return String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

const reais = (n: number) => n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const rotuloDoMes = (mes: string) => {
  const [a, m] = mes.split("-").map(Number);
  return `${MESES[m - 1]}/${String(a).slice(2)}`;
};

/** A folha do resumo executivo, em A4. Cor FIXA: papel não troca de tema (a doutrina da casa para documento). */
export function folhaDoResumo(r: ResumoExecutivo): string {
  const maiorMes = Math.max(1, ...r.meses.flatMap((m) => [m.previsto, m.recebido]));
  const barras = r.meses.map((m) => `<tr>
      <td>${escapar(rotuloDoMes(m.mes))}</td>
      <td class="n">${escapar(reais(m.previsto))}</td>
      <td><span class="barra previsto" style="width:${((m.previsto / maiorMes) * 100).toFixed(1)}%"></span></td>
      <td class="n">${escapar(reais(m.recebido))}</td>
      <td><span class="barra recebido" style="width:${((m.recebido / maiorMes) * 100).toFixed(1)}%"></span></td>
    </tr>`).join("\n");
  const etapas = r.porEtapa.filter((e) => e.quantas > 0).map((e) => `<tr><td>${escapar(`${e.etapa}. ${e.rotulo}`)}</td><td class="n">${escapar(e.quantas)}</td></tr>`).join("\n");
  const tribunais = r.porTribunal.map((t) => `<tr><td>${escapar(t.nome)}</td><td class="n">${escapar(t.quantas)}</td></tr>`).join("\n");
  const linhas = r.pericias.map((l) => `<tr>
      <td>${escapar(l.apelido || "—")}<div class="num">${escapar(l.numero)}</div></td>
      <td>${escapar(l.tribunal || "—")}${l.vara ? `<div class="num">${escapar(l.vara)}</div>` : ""}</td>
      <td>${escapar(l.tipo || "—")}</td>
      <td>${escapar(l.etapa)}</td>
      <td class="n">${escapar(reais(l.honorarios))}</td>
      <td class="n">${escapar(reais(l.recebido))}</td>
    </tr>`).join("\n");

  return `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8">
<title>Central do Perito — Resumo executivo</title>
<style>
  @page { size: A4; margin: 14mm; }
  body { font: 10px/1.4 -apple-system, "Segoe UI", Roboto, sans-serif; color: #14181f; }
  h1 { font-size: 17px; margin: 0 0 2px; color: #102850; }
  h2 { font-size: 12px; margin: 16px 0 6px; color: #102850; text-transform: uppercase; letter-spacing: .05em; }
  .sub { font-size: 10px; color: #5b6472; margin: 0 0 12px; }
  .kpis { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
  .kpi { border: 1px solid #d9dde4; border-radius: 6px; padding: 8px 10px; }
  .kpi span { display: block; font-size: 9px; color: #5b6472; text-transform: uppercase; letter-spacing: .04em; }
  .kpi strong { font-size: 15px; color: #102850; }
  .duas { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: 4px 6px; border-bottom: 1px solid #d9dde4; vertical-align: top; text-align: left; }
  th { background: #102850; color: #fff; font-size: 9px; text-transform: uppercase; letter-spacing: .04em; }
  td.n, th.n { text-align: right; white-space: nowrap; }
  .num { font-size: 9px; color: #5b6472; font-variant-numeric: tabular-nums; }
  .barra { display: inline-block; height: 8px; border-radius: 2px; min-width: 1px; }
  .barra.previsto { background: #9aa9c4; }
  .barra.recebido { background: #102850; }
  thead { display: table-header-group; }
  tr { break-inside: avoid; }
  footer { margin-top: 14px; font-size: 9px; color: #5b6472; }
</style>
</head>
<body>
<h1>Central do Perito — Resumo executivo</h1>
<p class="sub">${escapar(r.total)} perícia(s) · gerado em ${escapar(formatarData(r.geradoEm))}</p>

<div class="kpis">
  <div class="kpi"><span>Perícias</span><strong>${escapar(r.total)}</strong></div>
  <div class="kpi"><span>Em andamento</span><strong>${escapar(r.emAndamento)}</strong></div>
  <div class="kpi"><span>Concluídas</span><strong>${escapar(r.concluidas)}</strong></div>
  <div class="kpi"><span>Honorários</span><strong>${escapar(reais(r.honorarios))}</strong></div>
  <div class="kpi"><span>Recebido</span><strong>${escapar(reais(r.recebido))}</strong></div>
  <div class="kpi"><span>A receber</span><strong>${escapar(reais(r.aReceber))}</strong></div>
</div>

<h2>Previsto e recebido nos últimos 6 meses</h2>
<table>
<thead><tr><th>Mês</th><th class="n">Previsto</th><th></th><th class="n">Recebido</th><th></th></tr></thead>
<tbody>
${barras}
</tbody>
</table>
<p class="num">O previsto é o que vence no mês; o recebido, o que entrou no mês.</p>

<div class="duas">
  <div>
    <h2>Por etapa</h2>
    <table><thead><tr><th>Etapa</th><th class="n">Perícias</th></tr></thead><tbody>
${etapas || '<tr><td colspan="2">Nenhuma perícia.</td></tr>'}
    </tbody></table>
  </div>
  <div>
    <h2>Por tribunal</h2>
    <table><thead><tr><th>Tribunal</th><th class="n">Perícias</th></tr></thead><tbody>
${tribunais || '<tr><td colspan="2">Nenhuma perícia.</td></tr>'}
    </tbody></table>
  </div>
</div>

<h2>As perícias</h2>
<table>
<thead><tr><th>Processo</th><th>Tribunal e vara</th><th>Tipo</th><th>Etapa</th><th class="n">Honorários</th><th class="n">Recebido</th></tr></thead>
<tbody>
${linhas || '<tr><td colspan="6">Nenhuma perícia.</td></tr>'}
</tbody>
</table>

<footer>Central do Perito — Contabilidade Facilitada. Os números são os que estavam cadastrados no dia da geração.</footer>
</body>
</html>`;
}
